<template>
  <p
    :class="[
      'blinking-cursor-text',
      className,
      !textSelectableAfterTyping ? 'noselect' : '',
      isCompletedStep ? 'text-completed' : ''
    ]"
    :data-text="text"
  >
    <span v-for="(word, i) in splitWords" :data-word="word.text" class="word" :key="i">
      <span
        v-for="(letter, j) in word.text"
        :aria-live="isCurrentCharacter(word.startingIndex + j) ? 'assertive' : null"
        :class="getClasses(word.startingIndex + j, letter)"
        :key="letter + word.startingIndex + j"
        :role="isCurrentCharacter(word.startingIndex + j) ? 'input' : null"
        :tabindex="isCurrentCharacter(word.startingIndex + j) ? 0 : -1"
        style="position: relative;"
        >{{ letter }}</span
      >
      <!-- <span
          v-if="isCurrentCharacter(word.startingIndex + j)"
          style="height: 1px; opacity: 0; position: absolute; width: 1px"
          >step {{ textStep + 1 }} word {{ i + 1 }} position {{ word.startingIndex + j }} text - </span
        >{{ letter }}</span -->
    </span>
  </p>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { isBoolean, isNumber } from 'lodash';

const activity = createNamespacedHelpers('activity');
const typingActivity = createNamespacedHelpers('activity/typing');

export default {
  props: {
    text: {
      type: String,
      default: ''
    },
    className: {
      type: String
    },
    textStep: {
      type: Number,
      required: true
    },
    roundId: {
      type: Number
    },
    shouldBeCurrentStep: {
      type: Boolean | null
    },
    shouldBeCompletedStep: {
      type: Boolean | null
    },
    textSelectableAfterTyping: {
      type: Boolean,
      default: false
    },
    hideLettersAfterTypingIndex: {
      type: Boolean,
      default: false
    },
    enterOverride: Function,
    leaveOverride: Function,
    handleBeforeLeave: {
      type: Function,
      default: () => {}
    }
  },

  data() {
    return {};
  },

  computed: {
    ...activity.mapState(['progress']),
    ...typingActivity.mapState({
      typingProgress: 'progress',
      attemptedKey: 'attemptedKey'
    }),

    isCurrentStep() {
      const { roundIndex, stepIndex } = this.progress;
      if (isBoolean(this.shouldBeCurrentStep)) return this.shouldBeCurrentStep;
      // check for prop roundId
      return isNumber(this.roundId)
        ? stepIndex === this.textStep && roundIndex === this.roundId
        : stepIndex === this.textStep;
    },

    isCurrentCharacter() {
      return index => this.isCurrentStep && this.typingProgress.currentIndex === index;
    },

    isCompletedStep() {
      const { stepIndex, roundIndex, completedSteps } = this.progress;
      if (isBoolean(this.shouldBeCompletedStep)) return this.shouldBeCompletedStep;

      // check for prop roundId
      return isNumber(this.roundId)
        ? stepIndex > this.textStep && roundIndex === this.roundId
        : // Concentration activity solves steps in any order, hence the check for [].includes(step)
          stepIndex > this.textStep || completedSteps.includes(this.textStep);
    },

    isWrongKey() {
      return this.attemptedKey.character && !this.attemptedKey.isCorrect;
    },

    hasBeenCorrectlyTyped() {
      return index => this.isCurrentStep && this.typingProgress.currentIndex > index;
    },

    handleEnter() {
      if (this.enterOverride) return this.enterOverride;

      return this.tweenHandler(0, 1);
    },

    handleLeave() {
      if (this.enterOverride) return this.enterOverride;

      return this.tweenHandler(1, 0, 0.3);
    },

    splitWords() {
      const splitText = this.text.split(' ');
      const words = splitText.map((text, idx) => ({
        text: idx === splitText.length - 1 ? text : `${text} `
      }));

      let prevStartingIndex = null;
      words.forEach((w, i) => {
        const word = words[i];
        if (!prevStartingIndex) {
          prevStartingIndex = 0;
        }

        word.startingIndex = prevStartingIndex;
        // add space that was removed by split, except for last element
        // word.text = i === words.length - 1 ? word.text : word.text.concat(' ');
        prevStartingIndex += word.text.length;
      });

      return words;
    }
  },

  methods: {
    getClasses(index, letter) {
      const isCurrentCharacter = this.isCurrentCharacter(index);
      const charDone = this.hasBeenCorrectlyTyped(index) || this.isCompletedStep;
      const whitespace = letter === ' ' ? 'white-space' : 'prewrap';

      return [
        whitespace,
        `char${index + 1}`,
        {
          charDone,
          cursor: isCurrentCharacter,
          error: isCurrentCharacter && this.isWrongKey,
          hide: this.hideLettersAfterTypingIndex && !charDone,
          focusable: isCurrentCharacter
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped>
.noselect {
  -webkit-touch-callout: none;
  user-select: none;
  cursor: default;
}

.white-space {
  white-space: pre;
}

.prewrap {
  white-space: nowrap;
}

p,
p span {
  padding: 0;
  margin: 0;
  color: #757575;

  &.charDone {
    color: black;
  }

  &.error {
    color: #ee0000;
  }

  &.hide {
    color: #fff;
  }
}

.word {
  display: inline-flex;
}

.cursor {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 0;
    left: 0;
    bottom: 2px;
    border-bottom: 2px solid #000;
    animation: blinking 0.65s step-end infinite;
    animation-iteration-count: infinite;
  }

  &.error::after {
    border-bottom: 2px solid #f00;
  }

  &.blinky::after {
    border-bottom: 0;
  }
}

@keyframes blinking {
  from,
  to {
    border-bottom: 2px solid #000;
  }

  50% {
    border-bottom: 0px solid #000;
  }
}
</style>
